import { Tag } from "../../components/interfaces/Tag";
import { CellAddress, CoObjectItem, CoObjectSummary } from "../../components/interfaces/CoObject";
import { getTagsFromCustomXml } from "./CustomXmlCoTagHelper";
import { TruncatedText } from "../../components/generic/truncatedText/TruncatedText";
import { tokenizeScriptForRendering } from "../grammar/RuleGrammarParser";
import React from "react";
import { CoObjectTypeEnum } from "../../api/models";
import { formatMessage } from "./LanguageHelper";

const INVALID_TAG_LABEL = "Invalid Tag";

export type TagLabelRenderer = (label: string, index: number) => React.ReactNode;

function getObjectInfo(obj: CoObjectSummary, coObjectsById: Map<string, CoObjectSummary>) {
  if (obj.id.indexOf("_") != -1) {
    let key = obj.id.slice(0, obj.id.indexOf("_"));
    const base = coObjectsById.get(key);
    return { ...base, objectName: base.objectName, objectType: CoObjectTypeEnum.TABLE_CELL };
  } else {
    return coObjectsById.get(obj.id);
  }
}

export const getTags = async (coObjects: CoObjectItem[]): Promise<Tag[]> => {
  const coObjectsById = groupCoObjectsById(coObjects);
  const tags = await getTagsFromCustomXml();

  return tags.map((t) => ({
    ...t,
    objects: t.objects
      .filter((obj) => {
        if (obj.objectType === CoObjectTypeEnum.TABLE_CELL) {
          return obj.id.slice(0, obj.id.indexOf("_")) && coObjectsById.has(obj.id.slice(0, obj.id.indexOf("_")));
        } else {
          return obj.id && coObjectsById.has(obj.id);
        }
      })
      .map((obj) => {
        const info = getObjectInfo(obj, coObjectsById);
        return {
          id: obj.id,
          documentId: info?.documentId,
          contentControlId: info?.contentControlId,
          objectName: info?.objectName,
          objectType: obj.objectType ?? info?.objectType,
          row: obj.row,
          column: obj.column,
        };
      }),
  }));
};

const groupCoObjectsById = (coObjects: CoObjectItem[]) => {
  const coObjectsById = new Map<string, CoObjectSummary>();
  coObjects.forEach((obj) => {
    const key = obj.id;
    if (key) {
      coObjectsById.set(key, {
        documentId: obj.documentId,
        contentControlId: obj.contentControlId,
        id: obj.id,
        objectName: obj.info.objectName,
        objectType: obj.info.objectType,
      });
    }
  });

  return coObjectsById;
};

export const tagAsLabelRenderer: TagLabelRenderer = (label: string, index: number) => {
  const isInvalid = label === INVALID_TAG_LABEL;
  const commonStyles = {
    color: "white",
    borderRadius: 4,
    padding: "2px 6px",
    fontSize: 11,
    fontWeight: "bold" as const,
    margin: "0 2px",
    whiteSpace: "nowrap" as const,
    overflow: "hidden" as const,
    textOverflow: "ellipsis" as const,
    verticalAlign: "middle" as const,
    display: "inline-flex" as const,
    alignItems: "center" as const,
    gap: "4px",
  };

  const renderTagContent = (text: string, ref: React.RefObject<HTMLSpanElement>) => {
    const backgroundColor = isInvalid ? "var(--color-red)" : "var(--color-blue)";

    return (
      <span style={{ ...commonStyles, background: backgroundColor }}>
        {isInvalid && (
          <img src="/assets/warningRhombus.svg" alt="warning" style={{ width: 10, height: 10, marginLeft: 5 }} />
        )}
        <span
          ref={ref}
          style={{
            maxWidth: 120,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {text}
        </span>
      </span>
    );
  };

  return <TruncatedText key={`tag-${index}`} text={label} renderer={renderTagContent} />;
};

export const tagAsTextRenderer: TagLabelRenderer = (label: string) => label;

/**
 * Estrae tutti gli id univoci dei tag presenti in uno script,
 * usando la grammar ANTLR (senza regex).
 * Restituisce gli id senza le doppie graffe (es. "TAG_123").
 */
export function extractTagIdsFromScript(script: string): string[] {
  if (!script) return [];
  return tokenizeScriptForRendering(script)
    .filter((token) => token.type === "TAG")
    .map((token) => (token as { type: "TAG"; tagId: string }).tagId);
}

/**
 * Estrae tutti i tag (oggetti Tag) presenti in uno script,
 * filtrando dalla lista fornita.
 */
export function extractTagsFromScript(script: string, tags: Tag[]): Tag[] {
  const ids = new Set(extractTagIdsFromScript(script));
  return tags.filter((t) => ids.has(t.id));
}

export const replaceTagIdsWithLabels = (
  text: string,
  tags: Tag[],
  renderer: TagLabelRenderer = tagAsLabelRenderer
): React.ReactNode => {
  if (!tags || tags.length === 0) return text;
  const tagMap = new Map(tags.map((tag) => [tag.id, tag]));

  const tokens = tokenizeScriptForRendering(text);
  if (tokens.length === 0) return text;

  return tokens.map((token, index) => {
    if (token.type === "TAG") {
      const label = tagMap.get(token.tagId)?.label;
      return renderer(label ?? INVALID_TAG_LABEL, index);
    }
    return token.text;
  });
};

export const renderTextOrEmptyLabel = (text: string | undefined) => {
  if (text?.length) return text;
  return `(${formatMessage("cof.base.empty").toLowerCase()})`;
};

export const renderCellTitle = (address: CellAddress, text?: string, ignoreEmptyText?: boolean) => {
  const addressString = `(${address.row + 1}, ${address.column + 1})`;

  if (!text?.length && ignoreEmptyText) return addressString;
  return `${addressString} ${renderTextOrEmptyLabel(text)}`;
};
